import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import OptimizedVideoPlayer from './OptimizedVideoPlayer';
import { verifyCommandPassword } from '../utils/commandAuth';
import { copyToClipboard, loadEmbeddedVlcPlayer, resolveMemberStreamKey, buildStreamKey } from '../utils/dashboard';
import LiveStreamUrlPanel from './liveStream/LiveStreamUrlPanel';

const PIN_Z = 25000;
const UNPIN_Z = 9800;
const MIN_W = 360;
const MIN_H = 280;
const DEFAULT_W = 520;
const DEFAULT_H = 420;

function clampPos(x, y, w, h) {
  const maxX = Math.max(8, (window.innerWidth || 800) - Math.min(80, w * 0.4));
  const maxY = Math.max(8, (window.innerHeight || 600) - 48);
  return {
    x: Math.min(Math.max(8, x), maxX),
    y: Math.min(Math.max(8, y), maxY)
  };
}

/**
 * 指挥页队员直播：悬浮小窗（可拖、可缩放、置顶），无全屏遮罩，不挡指挥页其它操作。
 */
export default function LiveStreamModal({ open, member, onClose }) {
  const [urlsUnlocked, setUrlsUnlocked] = useState(false);
  const [showPwdForm, setShowPwdForm] = useState(false);
  const [pwdInput, setPwdInput] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [loadingPwd, setLoadingPwd] = useState(false);
  const [pinned, setPinned] = useState(true);
  const [size, setSize] = useState({ w: DEFAULT_W, h: DEFAULT_H });
  const [pos, setPos] = useState(() => ({
    x: Math.max(24, (typeof window !== 'undefined' ? window.innerWidth : 960) - DEFAULT_W - 32),
    y: Math.max(72, (typeof window !== 'undefined' ? Math.floor(window.innerHeight * 0.12) : 96))
  }));
  const dragRef = useRef(null);
  const resizeRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setUrlsUnlocked(false);
      setShowPwdForm(false);
      setPwdInput('');
      setPwdError('');
      setLoadingPwd(false);
    }
  }, [open, member?.name, member?.device_unique_id]);

  const unlockUrls = async () => {
    setLoadingPwd(true);
    try {
      const result = await verifyCommandPassword(pwdInput);
      if (!result.ok) {
        setPwdError(result.error || '密码错误，请重试');
        return;
      }
      setUrlsUnlocked(true);
      setShowPwdForm(false);
      setPwdInput('');
      setPwdError('');
    } catch (e) {
      setPwdError(e.message || '验证失败');
    } finally {
      setLoadingPwd(false);
    }
  };

  const loadEmbeddedVLC = () => {
    loadEmbeddedVlcPlayer({
      streamUrl: typeof window !== 'undefined' ? window.currentStreamUrls?.hls : undefined
    });
  };

  const onHeaderDown = useCallback((e) => {
    if (e.button !== 0) return;
    if (e.target && e.target.closest && e.target.closest('[data-live-no-drag]')) return;
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const origin = { ...pos };
    dragRef.current = { startX, startY, origin };
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const dx = ev.clientX - dragRef.current.startX;
      const dy = ev.clientY - dragRef.current.startY;
      setPos(clampPos(dragRef.current.origin.x + dx, dragRef.current.origin.y + dy, size.w, size.h));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pos, size.w, size.h]);

  const onResizeDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const origin = { ...size };
    resizeRef.current = { startX, startY, origin };
    const onMove = (ev) => {
      if (!resizeRef.current) return;
      const dw = ev.clientX - resizeRef.current.startX;
      const dh = ev.clientY - resizeRef.current.startY;
      const nextW = Math.min(Math.max(MIN_W, resizeRef.current.origin.w + dw), Math.max(MIN_W, (window.innerWidth || 800) - 16));
      const nextH = Math.min(Math.max(MIN_H, resizeRef.current.origin.h + dh), Math.max(MIN_H, (window.innerHeight || 600) - 16));
      setSize({ w: nextW, h: nextH });
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [size]);

  if (!open || !member) return null;

  const boundStreamKey = resolveMemberStreamKey(member) || buildStreamKey(member.name);
  const zIndex = pinned ? PIN_Z : UNPIN_Z;

  const node = (
    <div
      data-live-float="1"
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        width: size.w,
        height: size.h,
        zIndex,
        background: '#fff',
        borderRadius: 10,
        border: '1px solid #d0d7de',
        boxShadow: '0 12px 36px rgba(0,0,0,0.22)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        pointerEvents: 'auto'
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        onMouseDown={onHeaderDown}
        style={{
          padding: '8px 10px',
          borderBottom: '1px solid #e8e8e8',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          background: 'linear-gradient(180deg,#f7fafc,#eef3f8)',
          minHeight: 40,
          cursor: 'move',
          flexShrink: 0,
          userSelect: 'none'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flex: 1 }}>
          <span style={{ fontSize: 14, flexShrink: 0 }}>📡</span>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#333', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {member.name}
            <span style={{ fontWeight: 400, color: '#888', marginLeft: 6, fontSize: 12 }}>直播</span>
          </div>
        </div>
        <div data-live-no-drag="1" style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
          <label
            title={pinned ? '已置顶：始终最上层，点窗外不关闭' : '未置顶：可被其它浮层盖住'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12, color: '#445', userSelect: 'none' }}
          >
            <span>置顶</span>
            <span
              role="switch"
              aria-checked={pinned}
              onClick={(e) => {
                e.stopPropagation();
                setPinned((v) => !v);
              }}
              style={{
                width: 34,
                height: 18,
                borderRadius: 999,
                background: pinned ? '#1976d2' : '#c5ccd4',
                position: 'relative',
                transition: 'background .15s',
                display: 'inline-block'
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 2,
                  left: pinned ? 18 : 2,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: '#fff',
                  boxShadow: '0 1px 3px rgba(0,0,0,.25)',
                  transition: 'left .15s'
                }}
              />
            </span>
          </label>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose?.();
            }}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 18,
              cursor: 'pointer',
              color: '#888',
              padding: '0 4px',
              lineHeight: 1
            }}
            title="关闭直播"
          >
            ×
          </button>
        </div>
      </div>

      <div
        style={{
          flex: 1,
          padding: '8px 10px 10px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          minHeight: 0,
          overflow: 'auto'
        }}
      >
        <div
          style={{
            width: '100%',
            aspectRatio: '16/9',
            maxHeight: Math.max(160, size.h - 160),
            background: '#000',
            borderRadius: 6,
            overflow: 'hidden',
            position: 'relative',
            flexShrink: 0
          }}
        >
          <OptimizedVideoPlayer
            streamKey={boundStreamKey}
            memberName={member.name}
            orgName={member.org_name || ''}
            groupName={member.group_name || ''}
            batteryLevel={member.battery_level}
            protocol="webrtc"
            fallbackProtocol="flv"
            onError={(proto, err) => {
              console.error(`${proto}播放错误:`, err);
            }}
            onSuccess={(proto) => {
              console.log(`${proto}播放成功`);
            }}
            onLoadingChange={() => {}}
            style={{ width: '100%', height: '100%' }}
          />

          {urlsUnlocked && (
            <div
              style={{
                position: 'absolute',
                bottom: 10,
                left: 10,
                background: 'rgba(0,0,0,0.8)',
                borderRadius: 6,
                padding: 8,
                zIndex: 10
              }}
            >
              <div style={{ fontSize: 12, color: '#fff', marginBottom: 4 }}>外部播放器</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => {
                    const urls = window.currentStreamUrls;
                    if (urls) window.open(`vlc://${urls.hls}`, '_blank');
                  }}
                  style={{ background: '#ff6b35', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}
                  title="使用VLC播放器打开"
                >
                  VLC
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const urls = window.currentStreamUrls;
                    if (urls) copyToClipboard(urls.rtmp);
                  }}
                  style={{ background: '#007acc', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}
                  title="复制RTMP链接"
                >
                  RTMP
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const urls = window.currentStreamUrls;
                    if (urls) copyToClipboard(urls.flv);
                  }}
                  style={{ background: '#28a745', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer' }}
                  title="复制FLV链接"
                >
                  FLV
                </button>
              </div>
            </div>
          )}
        </div>

        <LiveStreamUrlPanel
          member={member}
          urlsUnlocked={urlsUnlocked}
          showPwdForm={showPwdForm}
          pwdInput={pwdInput}
          pwdError={pwdError}
          loadingPwd={loadingPwd}
          onToggleUrls={() => {
            if (urlsUnlocked) {
              setUrlsUnlocked(false);
              setShowPwdForm(false);
              setPwdError('');
              return;
            }
            setShowPwdForm(true);
            setPwdError('');
          }}
          onPwdChange={(value) => {
            setPwdInput(value);
            setPwdError('');
          }}
          onUnlock={unlockUrls}
          onCancelPwd={() => {
            setShowPwdForm(false);
            setPwdInput('');
            setPwdError('');
          }}
        />

        {urlsUnlocked && (
          <div
            style={{
              background: '#f8f9fa',
              borderRadius: 8,
              padding: 12,
              border: '1px solid #e9ecef',
              flexShrink: 0
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: '#333' }}>嵌入式播放器</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={loadEmbeddedVLC}
                style={{ background: '#ff6b35', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
              >
                嵌入VLC
              </button>
              <button
                type="button"
                onClick={() => {
                  const urls = window.currentStreamUrls;
                  if (urls) copyToClipboard(urls.hls);
                }}
                style={{ background: '#6c757d', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
              >
                复制HLS
              </button>
              <button
                type="button"
                onClick={() => {
                  const urls = window.currentStreamUrls;
                  if (urls) copyToClipboard(urls.rtmp);
                }}
                style={{ background: '#007acc', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
              >
                复制RTMP
              </button>
              <button
                type="button"
                onClick={() => {
                  const urls = window.currentStreamUrls;
                  if (urls) copyToClipboard(urls.flv);
                }}
                style={{ background: '#28a745', color: '#fff', border: 'none', padding: '6px 12px', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
              >
                复制FLV
              </button>
            </div>
          </div>
        )}
      </div>

      <div
        onMouseDown={onResizeDown}
        title="拖动调整大小"
        style={{
          position: 'absolute',
          right: 0,
          bottom: 0,
          width: 18,
          height: 18,
          cursor: 'nwse-resize',
          background: 'linear-gradient(135deg, transparent 50%, #90a4ae 50%)',
          borderBottomRightRadius: 10,
          zIndex: 5
        }}
      />
    </div>
  );

  if (typeof document === 'undefined' || !document.body) return node;
  return createPortal(node, document.body);
}
